import type { ErrorComponentProps } from "@tanstack/react-router";
import { signInWithRedirect } from "aws-amplify/auth";
import { AlertCircle, Loader2 } from "lucide-react";
import { type FC, type PropsWithChildren, useEffect } from "react";

import { m } from "@recobra/i18n/messages";
import { getLocale } from "@recobra/i18n/runtime";
import { Alert, AlertDescription, AlertTitle } from "@recobra/ui/alert";
import { Button } from "@recobra/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@recobra/ui/card";

import { hasReloadedForStaleChunk, isStaleChunkError, reloadForStaleChunk } from "../lib";
import { isAuthUnauthenticatedError } from "../lib";
import { getErrorMessage } from "../lib/api";
import { signInReturnPath } from "./signInReturnPath";

export const RouteError: FC<ErrorComponentProps> = ({ error }) => {
  const staleChunk = isStaleChunkError(error) && !hasReloadedForStaleChunk();

  useEffect(() => {
    clearOAuthErrorFromUrl();
    if (staleChunk) {
      reloadForStaleChunk();
      return;
    }
    if (isAuthUnauthenticatedError(error)) redirectToSignIn();
  }, [error, staleChunk]);

  if (staleChunk) {
    return (
      <SpinnerCard description={m.app_updating_description()} title={m.app_updating_title()} />
    );
  }

  if (isAuthUnauthenticatedError(error)) {
    return (
      <SpinnerCard
        description={m.auth_redirecting_description()}
        title={m.auth_redirecting_title()}
      />
    );
  }

  return (
    <ErrorCard description={m.app_error_description()} title={m.app_error_title()}>
      <Alert variant="destructive">
        <AlertCircle />
        <AlertTitle>{m.auth_error_title()}</AlertTitle>
        <AlertDescription>
          {error instanceof Error ? getErrorMessage(error) : m.app_error_fallback()}
        </AlertDescription>
      </Alert>
      <div className="flex flex-col gap-2">
        <Button className="w-full" onClick={() => window.location.reload()}>
          {m.app_reload_page()}
        </Button>
        <Button className="w-full" variant="outline" onClick={signInAgain}>
          {m.auth_sign_in()}
        </Button>
      </div>
    </ErrorCard>
  );
};

const ErrorCard: FC<
  PropsWithChildren<{
    description: string;
    title: string;
  }>
> = ({ children, description, title }) => (
  <ErrorPageShell>
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  </ErrorPageShell>
);

const ErrorPageShell: FC<PropsWithChildren> = ({ children }) => (
  <div className="flex min-h-svh items-center justify-center bg-background p-4">{children}</div>
);

const SpinnerCard: FC<{ description: string; title: string }> = ({ description, title }) => (
  <ErrorPageShell>
    <Card className="w-full max-w-md">
      <CardContent className="flex flex-col items-center gap-4 py-10 text-center">
        <Loader2 className="size-8 animate-spin text-primary" />
        <div className="space-y-1">
          <p className="font-medium">{title}</p>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </CardContent>
    </Card>
  </ErrorPageShell>
);

const clearOAuthErrorFromUrl = (): void => {
  if (typeof window === "undefined") {
    return;
  }
  const url = new URL(window.location.href);
  if (!url.searchParams.has("error") && !url.searchParams.has("error_description")) {
    return;
  }
  url.searchParams.delete("error");
  url.searchParams.delete("error_description");
  url.searchParams.delete("state");
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, "", next);
};

const signInAgain = (): void => {
  clearOAuthErrorFromUrl();
  redirectToSignIn();
};

const redirectToSignIn = (): void => {
  void signInWithRedirect({
    customState: signInReturnPath(),
    options: {
      lang: getLocale(),
    },
  });
};
